import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import { SearchBar } from './SearchBar';
import { DOC_SECTIONS } from '@/content/sections';
import { nexusColors } from '@/theme';

const NAV_LINKS = [
  { to: '/roadmap', label: 'Roadmap' },
  { to: '/feature-requests', label: 'Feature Requests' },
  { to: '/community', label: 'Community' },
];

export function AppHeader(): JSX.Element {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{ bgcolor: 'rgba(11,11,19,0.86)', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${nexusColors.line}` }}
    >
      <Toolbar sx={{ gap: 2, minHeight: 68 }}>
        <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
          <Box component="img" src="/brand/nexus-mark.svg" alt="" sx={{ height: 28, width: 28 }} />
          <Box sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 18, color: nexusColors.ink }}>
            NexusRMM <Box component="span" sx={{ color: nexusColors.purpleLight }}>Wiki</Box>
          </Box>
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ ml: 2, display: { xs: 'none', md: 'flex' } }}>
          {DOC_SECTIONS.slice(0, 6).map((s) => (
            <Button key={s.id} component={RouterLink} to={`/${s.slug}`} size="small" sx={{ color: nexusColors.muted, '&:hover': { color: nexusColors.ink } }}>
              {s.title}
            </Button>
          ))}
        </Stack>

        <Box sx={{ flex: 1 }} />

        <Box sx={{ width: { xs: 140, sm: 260 }, display: { xs: 'none', sm: 'block' } }}>
          <SearchBar onSubmit={(q) => navigate(`/search?q=${encodeURIComponent(q)}`)} />
        </Box>

        <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
          {NAV_LINKS.map((l) => (
            <Button key={l.to} component={RouterLink} to={l.to} size="small" variant="outlined" sx={{ borderColor: nexusColors.line, color: nexusColors.ink }}>
              {l.label}
            </Button>
          ))}
        </Stack>

        <IconButton
          aria-label="Open navigation"
          onClick={() => setOpen(true)}
          sx={{ display: { xs: 'inline-flex', md: 'none' }, color: nexusColors.ink }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 280, bgcolor: nexusColors.bgElevated, height: '100%', pt: 2 }}>
          <Box sx={{ px: 2, pb: 1 }}>
            <SearchBar onSubmit={(q) => { setOpen(false); navigate(`/search?q=${encodeURIComponent(q)}`); }} />
          </Box>
          <Divider sx={{ borderColor: nexusColors.line, my: 1 }} />
          <List>
            {DOC_SECTIONS.map((s) => (
              <ListItemButton key={s.id} component={RouterLink} to={`/${s.slug}`} onClick={() => setOpen(false)}>
                <ListItemText primary={s.title} />
              </ListItemButton>
            ))}
          </List>
          <Divider sx={{ borderColor: nexusColors.line, my: 1 }} />
          <List>
            {NAV_LINKS.map((l) => (
              <ListItemButton key={l.to} component={RouterLink} to={l.to} onClick={() => setOpen(false)}>
                <ListItemText primary={l.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
